import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, BedDouble, Car, ChevronDown, Compass, Heart, MapPin, Plane, Train, TramFront } from "lucide-react";
import { STAYS_BY_BUDGET, THINGS_TO_DO, COUPLE_NOTE, VENUE_PHOTOS, VENUE_ADDRESS, VENUE_MAP_EMBED, TRAVEL_TIMES, mapsLink } from "@/lib/wedding-data";

const TRAVEL_ICONS = { plane: Plane, car: Car, train: Train, metro: TramFront };

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export default function Logistics() {
  const [budget, setBudget] = useState("medium");
  const [openTodo, setOpenTodo] = useState("sightseeing");
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % VENUE_PHOTOS.length), 4000);
    return () => clearInterval(t);
  }, []);
  return (
    <section
      id="logistics"
      data-testid="logistics-section"
      className="scroll-mt-24 py-28 md:py-36 bg-white/50 border-y border-[#D4AF37]/20"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-6">
            Plan Your Trip
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1A1A] leading-[1.05]">
            Logistics, <span className="italic text-[#5D171E]">sorted</span>
          </h2>
        </div>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7 }}
          className="mb-16 grid md:grid-cols-2 overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#FDFBF7]"
        >
          <div
            className="relative h-72 md:h-auto md:min-h-[380px] overflow-hidden"
            data-testid="venue-slideshow"
          >
            {VENUE_PHOTOS.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={`Le Luxe Resort, Gurgaon - photo ${i + 1}`}
                loading="lazy"
                data-testid={`venue-slide-${i}`}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  i === slide ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {VENUE_PHOTOS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSlide(i)}
                  data-testid={`venue-slide-dot-${i}`}
                  aria-label={`Show venue photo ${i + 1}`}
                  className={`h-2 w-2 rounded-full transition-colors duration-300 ${
                    i === slide ? "bg-[#D4AF37]" : "bg-white/60"
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.3em] text-[#5D171E]/60 mb-4">
              The Venue
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] tracking-tight">
              Lelux Resort, Gurgaon
            </h3>
            <p className="mt-4 text-sm text-[#1A1A1A]/70 leading-relaxed">
              Every event from the engagement onward takes place here - lawns,
              banquets and rooms under one roof, so you never have to rush
              between functions.
            </p>
            <p
              data-testid="venue-address"
              className="mt-4 flex items-start gap-3 text-sm text-[#5D171E] leading-relaxed border-l-2 border-[#D4AF37] pl-4"
            >
              {VENUE_ADDRESS}
            </p>
            <a
              href={mapsLink(VENUE_ADDRESS)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="venue-map-link"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5D171E] border-b border-[#D4AF37] pb-1 w-fit hover:text-[#471016] transition-colors duration-300"
            >
              <MapPin className="h-4 w-4" /> Open in Google Maps
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href="/card"
              data-testid="directions-card-link"
              className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5D171E] border-b border-[#D4AF37] pb-1 w-fit hover:text-[#471016] transition-colors duration-300"
            >
              Save the directions card
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </motion.div>

        <div className="mb-16 grid lg:grid-cols-5 gap-6 items-stretch">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3 overflow-hidden rounded-2xl border border-[#D4AF37]/30 min-h-[380px]"
          >
            <iframe
              title="Map to Lelux Resort, Gurgaon"
              data-testid="venue-map-embed"
              src={VENUE_MAP_EMBED}
              className="w-full h-full min-h-[380px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
          <div className="lg:col-span-2 flex flex-col">
            <h3 className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-6">
              <Car className="h-4 w-4" /> Getting Here
            </h3>
            <div className="flex-1 flex flex-col gap-4">
              {TRAVEL_TIMES.map((t, i) => {
                const Icon = TRAVEL_ICONS[t.icon];
                return (
                  <motion.div
                    key={t.label}
                    {...fadeUp}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    data-testid={`travel-card-${i}`}
                    className="flex-1 rounded-2xl border border-[#D4AF37]/30 bg-[#FDFBF7] px-6 py-5 flex items-center gap-5"
                  >
                    <div className="h-11 w-11 shrink-0 rounded-full bg-[#5D171E] text-[#E1C699] flex items-center justify-center">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A1A1A]">{t.label}</p>
                      <p className="text-xs text-[#1A1A1A]/55">{t.note}</p>
                    </div>
                    <span className="font-serif italic text-xl text-[#5D171E] whitespace-nowrap">
                      {t.time}
                    </span>
                  </motion.div>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-[#1A1A1A]/50">
              Drive times are approximate and off-peak - wedding-season traffic
              may add a little.
            </p>
          </div>
        </div>

        <div className="mb-16">
          <motion.h3
            {...fadeUp}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-4"
          >
            <BedDouble className="h-4 w-4" /> Nearby Stays - Within ~20 Minutes
          </motion.h3>
          <p className="text-sm text-[#1A1A1A]/60 mb-8 max-w-xl">
            Pick your budget and we'll point you to comfortable options close to
            the venue.
          </p>
          <div className="flex flex-wrap gap-3 mb-10">
            {STAYS_BY_BUDGET.map((b) => (
              <button
                key={b.key}
                onClick={() => setBudget(b.key)}
                data-testid={`stay-budget-${b.key}`}
                className={`rounded-full px-6 py-3 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  budget === b.key
                    ? "bg-[#5D171E] text-[#FDFBF7] border-[#5D171E]"
                    : "bg-transparent text-[#5D171E] border-[#D4AF37]/50 hover:border-[#5D171E]"
                }`}
              >
                {b.label} <span className="opacity-70">· {b.range}</span>
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {STAYS_BY_BUDGET.find((b) => b.key === budget).hotels.map((h, i) => (
              <motion.div
                key={h.name}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`hotel-card-${i}`}
                className="rounded-2xl border border-[#D4AF37]/30 bg-[#FDFBF7] p-8 flex flex-col"
              >
                <h4 className="font-serif text-2xl text-[#1A1A1A] tracking-tight leading-snug">
                  {h.name}
                </h4>
                <p className="mt-3 text-sm text-[#1A1A1A]/65 leading-relaxed flex-1">
                  {h.note}
                </p>
                <a
                  href={mapsLink(h.query)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`hotel-map-link-${i}`}
                  className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5D171E] hover:text-[#471016] transition-colors duration-300"
                >
                  View on Maps <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#1A1A1A]/50">
            Distances are approximate. We recommend booking early - late January
            is peak wedding season in Gurgaon.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <motion.h3
              {...fadeUp}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-4"
            >
              <Compass className="h-4 w-4" /> Things To Do Around
            </motion.h3>
            <p className="text-sm text-[#1A1A1A]/60 mb-8">
              First time in Delhi? Start with Sightseeing - it has the classics.
            </p>
            <div className="space-y-4">
              {THINGS_TO_DO.map((cat) => (
                <div
                  key={cat.key}
                  className="rounded-2xl border border-[#D4AF37]/30 bg-[#FDFBF7] overflow-hidden"
                >
                  <button
                    onClick={() => setOpenTodo(openTodo === cat.key ? null : cat.key)}
                    data-testid={`todo-dropdown-${cat.key}`}
                    className="w-full px-8 py-6 flex items-center justify-between text-left"
                  >
                    <span className="font-serif text-2xl text-[#1A1A1A] tracking-tight">
                      {cat.label}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 text-[#D4AF37] transition-transform duration-300 ${
                        openTodo === cat.key ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openTodo === cat.key && (
                    <div className="px-8 pb-6 space-y-5">
                      {cat.items.map((t, i) => (
                        <div key={t.name} data-testid={`todo-item-${cat.key}-${i}`} className="flex items-baseline gap-4">
                          <span className="font-serif italic text-lg text-[#D4AF37]/70">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <div>
                            <p className="font-medium text-sm text-[#1A1A1A]">{t.name}</p>
                            <p className="mt-0.5 text-sm text-[#1A1A1A]/60">{t.note}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <motion.h3
              {...fadeUp}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5D171E] mb-8"
            >
              <Heart className="h-4 w-4" /> {COUPLE_NOTE.title}
            </motion.h3>
            <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#5D171E] p-8 md:p-12">
              <p className="font-serif text-2xl md:text-3xl text-[#E1C699] leading-snug tracking-tight">
                {COUPLE_NOTE.lines[0]}
              </p>
              <div className="my-8 h-px bg-[#D4AF37]/25" />
              <p className="font-serif italic text-xl text-[#FDFBF7]">
                {COUPLE_NOTE.signoff}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
